import { Grid, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import CheckboxGroupField from "components/CheckboxGroupField";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {Button,CollapsibleSection,MuiTable,StatusChip,TextValidator,ValidatorForm,ConfirmDialog,Box,TableTopSearch,SingleViewDialog,} from "components";
import DistrictDropDown from "AutoComplites/DistrictDropDown";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import { scrollToTop } from "Utils";
import BranchesServices from "services/BranchesServices";
import DistrictServices from "services/DistrictServices";

const initial_form = {
  name: "",
  description: "",
  contact_no: "",
  code: "",
  address_line1: "",
  address_line2: "",
  address_line3: "",
  is_main: false,
  status: "Active",
  "order[0]": ["updatedAt", "DESC"],
};

const AddOGrgSetupBranch = ({ org }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [singleData, setSingleData] = useState(null);

  const handleViewBranch = (branch) => {
    setSingleData(branch);
    setOpenDialog(true);
  };
  const [formData, setFormData] = useState(initial_form);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    page: 0,
    limit: 10,
    "order[0]": ["updatedAt", "DESC"],
    search: null,
    district: "",
  });
  const [totalItems, setTotalItems] = useState(0);

  const [allDistricts, setAllDistricts] = useState([]);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await DistrictServices.getAll({ limit: 1000, status: "Active" });
        const list = res?.data?.view?.data || res?.data?.data || [];
        const mapped = Array.isArray(list)
          ? list.map((d) => ({ id: d.id ?? d, name: d.name ?? d }))
          : [];
        setAllDistricts(mapped);
      } catch (err) {
        setAllDistricts([]);
      }
    };
    fetchDistricts();
  }, []);

  const handleChangeStatus = async (id, status) => {
    const actionText = status === "Active"
      ? "deactivate this branch"
      : "activate this branch";
    ConfirmDialog({
      type: status === "Active" ? "error" : "success",
      title: `Are you sure you want to ${actionText}?`,
      description:
        status === "Active"
          ? "This will deactivate the branch. You can reactivate it later."
          : "This will activate the branch. You can deactivate it later.",
      buttons: [
        {
          label: "No",
          onClick: () => toast.info("Action cancelled."),
        },
        {
          label: "Yes",
          onClick: async () => {
            const newStatus = status === "Active" ? "Inactive" : "Active";
            setLoading(true);
            try {
              const response = await BranchesServices.updateStatus(id, {
                status: newStatus,
              });
              if (response.success) {
                toast.success(response?.message || `Branch status changed to ${newStatus}!`);
                fetchdata();
              } else {
                toast.error(response?.message || "Failed to update status.");
              }
            } catch (err) {
              const msg = err?.response?.data?.message || err?.message || "Error updating status. Please try again.";
              toast.error(msg);
            } finally {
              setLoading(false);
            }
          },
        },
      ],
    });
  };

  const fetchdata = useCallback(async () => {
    setLoading(true);
    try {
      const districtParam = (() => {
        if (!filters.district) return undefined;
        const match = (allDistricts || []).find(
          (d) => String(d.id) === String(filters.district)
        );
        return match ? match.name : filters.district;
      })();

      const params = {
        organization_id: org?.id,
        search: filters.search,
        page: filters.page,
        limit: filters.limit,
        district: districtParam || undefined,
        "order[0]": filters["order[0]"],
      };
      const response = await BranchesServices.get(params);
      const total =
        response?.data?.view?.totalItems || response?.data?.totalItems || 0;
      setTotalItems(total);
      const branches = response?.data?.view?.data || response?.data?.data || [];
      setData(Array.isArray(branches) ? branches : []);
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Failed to fetch branches. Please try again.";
      toast.error(msg);
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [filters, org, allDistricts]);

  useEffect(() => {
    fetchdata();
  }, [fetchdata, org]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page - 1,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 0,
      limit: pageSize,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const orgId = org?.id;
      if (!orgId) {
        toast.error("Organization is required!");
        setSubmitting(false);
        return;
      }

      const existingBranchWithCode = (data || []).find(
        (branch) =>
          branch.organization_id === orgId &&
          branch.code?.toLowerCase() === formData.code?.toLowerCase() &&
          (!isEdit || branch.id !== editingData)
      );

      if (existingBranchWithCode) {
        toast.error(`Branch Code "${formData.code}" already exists for this organization!`);
        setSubmitting(false);
        return;
      }
      const districtForApi = (() => {
        const d = formData.district || formData.District || formData.district || null;
        if (!d) return null;
        const matchById = (allDistricts || []).find((it) => String(it.id) === String(d));
        if (matchById) return matchById.name;
        if (typeof d === "string") return d;
        return d;
      })();

      const apiData = {
        name: formData.name,
        description: formData.description,
        address_line1: formData.addressLine1 || "",
        address_line2: formData.addressLine2 || "",
        address_line3: formData.addressLine3 || "",
        district: districtForApi,
        contact_no: formData.phone || formData.contact_no || "",
        code: formData.code,
        is_main: Array.isArray(formData.mainBranch)
          ? formData.mainBranch.includes("main")
          : formData.is_main || false,
        status: formData.status || "Active",
        organization_id: orgId,
      };
      let response;
      if (isEdit) {
        response = await BranchesServices.update(editingData, apiData);
        if (response.success) {
          toast.success(response?.message || "Branch updated successfully!");
        } else {
          toast.error(response?.message || "Main branch already exists for this organization");
        }
      } else {
        response = await BranchesServices.create(apiData);
        if (response.success) {
          toast.success(response?.message || "Branch created successfully!");
        } else {
          toast.error(response?.message || "Failed to create. Please try again.");
        }
      }
      if (response.success) {
        setFormData(initial_form);
        setIsEdit(false);
        setOpenEditModal(false);
        setEditingData(null);
        fetchdata();
        scrollToTop();
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "An unexpected error occurred. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
 
        <CollapsibleSection
          title="All branches"
          collapsTrigger={openEditModal}
          buttonText="Add new branch"
        >
          <ValidatorForm onSubmit={handleSubmit} process={submitting}>
            <Grid container spacing={4}>
              <Grid size={{ lg: 6, md: 6, sm: 6, xs: 12 }} sx={{ mt: 2 }}>
                <Box>
                  <Grid container spacing={1}>
                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        label="Branch Name"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleInputChange}
                        validators={["required"]}
                        errorMessages={["This field is required"]}
                        required={true}
                        disabled={loading}
                        placeholder="Branch name"
                      />
                    </Grid>

                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        label="Description"
                        name="description"
                        multiline
                        rows={4}
                        value={formData.description || ""}
                        onChange={handleInputChange}
                        validators={["required"]}
                        errorMessages={["This field is required"]}
                        required={true}
                        disabled={loading}
                        placeholder="Description"
                      />
                    </Grid>

                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        label="Phone"
                        name="phone"
                        type="tel"
                        value={formData.phone || ""}
                        onChange={handleInputChange}
                        validators={[
                          "required",
                          "matchRegexp:^\\d+$",
                          "maxStringLength:10",
                        ]}
                        errorMessages={[
                          "This field is required",
                          "Only numbers are allowed",
                          "Maximum length is 10 digits",
                        ]}
                        required
                        disabled={loading}
                        placeholder="Enter phone number"
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>

                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        label="Branch Code"
                        name="code"
                        value={formData.code || ""}
                        onChange={handleInputChange}
                        validators={["required"]}
                        errorMessages={["This field is required"]}
                        required={true}
                        disabled={loading}
                        placeholder="Branch Code"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid size={{ lg: 6, md: 6, sm: 6, xs: 12 }} sx={{ mt: 2 }}>
                <Box>
                  <Grid container spacing={2}>
                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        label="Address of the organization"
                        name="addressLine1"
                        value={formData.addressLine1 || ""}
                        onChange={handleInputChange}
                        validators={["required"]}
                        errorMessages={["This field is required"]}
                        required
                        disabled={loading}
                        placeholder="Line 1"
                      />
                    </Grid>
                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        name="addressLine2"
                        value={formData.addressLine2 || ""}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="Line 2"
                      />
                    </Grid>
                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <TextValidator
                        name="addressLine3"
                        value={formData.addressLine3 || ""}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="Line 3"
                      />
                    </Grid>
                    <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                      <DistrictDropDown
                        name="district"
                        label="District"
                        value={formData.district ? { id: formData.district, name: formData.district } : ""}
                        options={allDistricts}
                        onChange={(event, newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            district: newValue ? newValue.id : "",
                          }));
                        }}
                        required
                        disabled={loading}
                        placeholder="District"
                      />
                      <CheckboxGroupField
                        name="mainBranch"
                        checkboxes={[
                          { value: "main", label: "Make as main branch" },
                        ]}
                        row={true}
                        required={false}
                        value={formData.mainBranch || []}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          const otherMainBranch = (data || []).find(
                            (b) =>
                              b.organization_id === org?.id &&
                              b.is_main &&
                              (!isEdit || b.id !== editingData)
                          );
                          if (checked) {
                            if (otherMainBranch) {
                              toast.error(`A main branch already exists for this organization (${otherMainBranch.name}).`);
                              return;
                            }
                            setFormData((prev) => ({
                              ...prev,
                              mainBranch: ["main"],
                            }));
                          } else {
                            setFormData((prev) => ({
                              ...prev,
                              mainBranch: [],
                            }));
                          }
                        }}
                        disabled={loading}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            <Grid className="mt-2" container spacing={1}>
              <Grid>
                <Button type="submit" process={submitting}>
                  {isEdit ? "UPDATE" : "SAVE"}
                </Button>
              </Grid>
              <Grid>
                {openEditModal && (
                  <Button
                    className="btn-warning"
                    onClick={() => {
                      setFormData(initial_form);
                      setIsEdit(false);
                      setOpenEditModal(false);
                      setEditingData(null);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Grid>
            </Grid>
          </ValidatorForm>
        </CollapsibleSection>

        <div>  
          <ValidatorForm> 
            <Grid container spacing={1} alignItems="center">
              <Grid size={{ lg: 3, md: 6, sm: 12, xs: 12 }}>
                <DistrictDropDown
                  name="District select"
                  label="District"
                  value={filters.district || ""}
                  options={allDistricts}
                  onChange={(event, newValue) => {
                    setFilters((prev) => ({
                      ...prev,
                      district: newValue ? newValue.id : "",
                      page: 0,
                    }));
                  }}
                  disabled={loading}
                />
              </Grid>
              
              <Grid
                size={{ lg: 3, md: 3, sm: 6, xs: 12 }} >
                <Button
                  variant="outlined" color="error"
className=" mt-5"
                  onClick={() => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      district: "",
                      page: 0,
                      search: "",
                    }));
                  }}
                  sx={{ height: 40, minWidth: 90, boxSizing: "border-box" }}
                >
                  <FilterAltOutlinedIcon sx={{ mr: 1 }} />
                  Clear
                </Button>
              </Grid>
            </Grid>
            <Grid size={{ lg: 3, md: 6, sm: 6, xs: 12 }}>
              <TableTopSearch
                title='Branches'
                placeholder='Search by branch name'
                searchText={filters.search || ""}
                showAddButton={false}
                showSearch={true}
                showTotalWithTitle={true}
                rowCount={totalItems}
                onSubmitSearch={null}
                onSearchChange={(e) => {
                  setFilters(prevData => ({
                    ...prevData,
                    search: e.target.value
                  }));
                }}
              />
            </Grid>
          </ValidatorForm>

          <MuiTable
            columns={[
              {
                accessorKey: "name",
                header: "Name",
                Cell: ({ row }) => <span>{row.original.name || ""}</span>,
              },
              {
                accessorKey: "code",
                header: "Branch Code",
                Cell: ({ row }) => <span>{row.original.code || ""}</span>,
              },
              {
                accessorKey: "contact_no",
                header: "Phone",
                Cell: ({ row }) => <span>{row.original.contact_no || ""}</span>,
              },
              {
                accessorKey: "address_line1",
                header: "Address",
                Cell: ({ row }) => (
                  <span>{row.original.address_line1 || ""}</span>
                ),
              },
              {
                accessorKey: "district",
                header: "District",
                Cell: ({ row }) => <span>{row.original.district || ""}</span>,
              },
              {
                accessorKey: "status",
                header: "Status",
                Cell: ({ row }) => {
                  return (
                    <span
                      style={{ cursor: "pointer" }}
                      title={
                        row.original.status === "Active"
                          ? "Click to set Inactive"
                          : "Click to set Active"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChangeStatus(
                          row.original.id,
                          row.original.status
                        );
                      }}
                    >
                      <StatusChip status={row.original.status || ""} />
                    </span>
                  );
                },
              },
              {
                accessorKey: "actions",
                header: "Actions",
                Cell: ({ cell }) => (
                  <div style={{ display: "flex", gap: 8 }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => {
                          setIsEdit(false);
                          setOpenEditModal(false);
                          setTimeout(() => {
                            const branch = cell.row.original;
                            const editData = {
                              name: branch.name || "",
                              description: branch.description || "",
                              phone: branch.contact_no || "",
                              code: branch.code || "",
                              addressLine1: branch.address_line1 || "",
                              addressLine2: branch.address_line2 || "",
                              addressLine3: branch.address_line3 || "",
                              district: branch.district || "",
                              mainBranch: branch.is_main ? ["main"] : [],
                              status: branch.status || "Active",
                            };
                            setFormData(editData);
                            setIsEdit(true);
                            setOpenEditModal(true);
                            setEditingData(branch.id);
                            scrollToTop();
                          }, 500);
                        }}
                      >
                        <DriveFileRenameOutlineOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewBranch(cell.row.original)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                ),
                headerProps: { style: { textAlign: "right" } },
                cellProps: { style: { textAlign: "right" } },
              },
            ]}
            data={data}
            title="All Branches"
            manualPagination={true}
            rowCount={totalItems}
            currentPage={filters.page}
            pageSize={filters.limit}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
          />
          <SingleViewDialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            data={{
              title: singleData?.name,
              otherData: [
                {
                  label: "Status",
                  value: singleData?.status
                },       
                {
                  label: "Phone",
                  value: singleData?.contact_no
                },
                  {
                  label: "Branch Code",
                  value: singleData?.code
                },
               {
                  label: "Address",
                  value: [singleData?.address_line1, singleData?.address_line2, singleData?.address_line3]
                    .filter(line => line && line.trim())
                    .join(' ') || 'N/A'
                },
                                {
                  label: "Main Branch",
                  value: singleData?.is_main ? "Yes" : "No"
                },
                {
                  label: "Description",
                  value: singleData?.description
                },             
                {
                  label: "District",
                  value: singleData?.district
                }
              ]
            }}
          />
        </div> 
    </div>
  );
};

export default AddOGrgSetupBranch;